import React, {RefObject, useContext} from 'react';
import {StateContext} from '../ContextProvider';
import {Box} from '@mui/material';
import MapDraw from '../components/MapDraw';
import TopoDraw from '../components/TopoDraw';

export const PRINT_SIZE = {
    mobile: {
        window: {
            width: '320px',
            height: '440px',
        },
        title: {
            fontSize: '16px',
            lineHeight: 0,
            fontWeight: 'bold'
        },
        fitBoundsPadding: {
            top: 30,
            right: 30,
            bottom: 30,
            left: 30
        },
        fitBoundsPaddingWithTopo: {
            top: 30,
            right: 30,
            bottom: 100,
            left: 30
        },
        frame: {
            padding: 20
        },
        topo: {
            bottom: 0
        },
        canvaTopo: {
            width: '100%',
            minWidth: '100%',
            height: '80px'
        },
        lineStroke: {
            width: 2,
        },
        small: {
            fontSize: '10px',
        },
        text: {
            fontSize: '12px',
        },
        time: {
            right: 45
        },
        raceName: {
            paddingTop: '8px',
            marginBlockStart: '8px',
            marginBlockEnd: '8px',
            lineHeight: 0,
        },
        userName: {
            marginRight: '3px',
            fontSize: '10px',
            marginBottom: '2px',
        },
        timeLabel: {
            fontSize: '20px',
        }
    },
    desktop: {
        window: {
            width: '400px',
            height: '533px'
        },
        title: {
            fontSize: '20px',
            lineHeight: 0,
            fontWeight: 'bold'
        },
        fitBoundsPadding: {
            top: 50,
            right: 50,
            bottom: 50,
            left: 50
        },
        fitBoundsPaddingWithTopo: {
            top: 50,
            right: 50,
            bottom: 120,
            left: 50
        },
        frame: {
            padding: 25
        },
        topo: {
            bottom: 2
        },
        canvaTopo: {
            width: '400px',
            height: '70px'
        },
        lineStroke: {
            width: 3,
        },
        small: {
            fontSize: '12px',
        },
        text: {
            fontSize: '16px',
        },
        time: {
            right: 50
        },
        raceName: {
            paddingTop: '10px',
            marginBlockStart: '10px',
            marginBlockEnd: '10px',
            lineHeight: 0,
        },
        userName: {
            marginRight: '6px',
            fontSize: '12px',
            marginBottom: '1px',
        },
        timeLabel: {
            fontSize: '27px',
        }
    },
    a6: {
        window: {
            width: '1240px',
            height: '1748px'
        },
        title: {
            fontSize: '50px',
            fontWeight: 'bold',
            lineHeight: 0,
        },
        fitBoundsPadding: {
            top: 100,
            right: 100,
            bottom: 100,
            left: 100
        },
        fitBoundsPaddingWithTopo: {
            top: 100,
            right: 100,
            bottom: 300,
            left: 100
        },
        frame: {
            padding: 50
        },
        topo: {
            bottom: 12
        },
        canvaTopo: {
            width: '1240px',
            height: '200px'
        },
        lineStroke: {
            width: 5,
        },
        small: {
            fontSize: '30px',
        },
        text: {
            fontSize: '40px',
        },
        time: {
            right: 100
        },
        raceName: {
            paddingTop: '20px',
        },
        userName: {
            marginRight: '9px',
            fontSize: '30px',
        },
        timeLabel: {
            fontSize: '27px',
        }
    },
    a4: {
        window: {
            width: '3543px',
            height: '4724px'
        },
        title: {
            fontSize: '180px',
            fontWeight: 'bold',
            lineHeight: 0
        },
        frame: {
          padding: 200
        },
        topo: {
            bottom: 70
        },
        canvaTopo: {
            width: '3543px',
            height: '560px'
        },
        fitBoundsPadding: {
            top: 300,
            right: 300,
            bottom: 300,
            left: 300
        },
        fitBoundsPaddingWithTopo: {
            top: 300,
            right: 300,
            bottom: 700,
            left: 300
        },
        lineStroke: {
            width: 15,
        },
        small: {
            fontSize: '90px',
        },
        text: {
            fontSize: '120px',
        },
        time: {
            right: 350
        },
        raceName: {
            paddingTop: '20px',
        },
        userName: {
            marginRight: '12px',
            fontSize: '80px',
        },
        timeLabel: {
            fontSize: '27px',
        }
    },
};

const Theme = ({refObject, size} : {refObject: RefObject<HTMLDivElement> | null, size: 'a4' | 'a6' | 'desktop' | 'mobile'}) => {
    const {raceData} = useContext(StateContext);

    const theme = {
        backgroundColor: raceData.cartography === 'full_black' ? '#000' : '#fff',
        color: '#000',
        colorSecondary: '#fff',
        opacity: 0.85,
        sizePrint: size
    };

    const showTopo = raceData.withTopo && raceData.cartography !== 'full_black' && raceData.cartography !== 'full_white';

    return (
        <Box style={{
            boxShadow: '5px 5px 15px rgba(0, 0, 0, 0.2)',
            width: `calc(${PRINT_SIZE[size].window.width} + ${PRINT_SIZE[size].frame.padding*2}px)`,
            margin: 'auto',
            marginTop: size === 'mobile' ? 30 : 'auto'
        }}>
        <Box style={{
            position: 'relative',
            display: 'block',
            ...PRINT_SIZE[size].window,
            padding: PRINT_SIZE[size].frame.padding,
            backgroundColor: theme.backgroundColor,
            overflow: 'hidden',
        }} ref={refObject}>
            <Box width={'100%'} height={'80%'} style={{position: 'relative'}}>
                <MapDraw fitBoundsPadding={showTopo ? PRINT_SIZE[size].fitBoundsPaddingWithTopo : PRINT_SIZE[size].fitBoundsPadding}
                         lineStroke={PRINT_SIZE[size].lineStroke.width}/>
                {showTopo&&
                    <TopoDraw size={size} />
                }
            </Box>
            <div>
                <Box style={{
                    position: 'absolute',
                    height: '20%',
                    left: PRINT_SIZE[size].frame.padding,
                    right: PRINT_SIZE[size].frame.padding,
                    bottom: PRINT_SIZE[size].frame.padding,
                    backgroundColor: theme.backgroundColor,
                    color: raceData.textColor,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                }}>
                    <div style={{...PRINT_SIZE[theme.sizePrint].userName, fontWeight: 'bold'}}
                         className={'rotate-text'}>
                        {raceData.firstname.toUpperCase()}
                    </div>
                    <div>
                        <h2 style={{...PRINT_SIZE[size].title, ...PRINT_SIZE[size].raceName}}>{raceData.raceName}</h2>
                    </div>
                    <div>
                        <div style={{...PRINT_SIZE[size].small}}>
                            {String(raceData.day).padStart(2, '0')}.{String(raceData.mounth).padStart(2, '0')}.{raceData.year}
                        </div>
                        <div style={{...PRINT_SIZE[size].text}}>
                            <b>{raceData.raceDistance}</b> km
                        </div>
                        {raceData.hike > 0 &&
                            <div style={{...PRINT_SIZE[size].text}}>
                                <b>{raceData.hike}m</b> D+
                            </div>
                        }
                    </div>
                    {raceData.more &&
                        <span style={{...PRINT_SIZE[size].small, lineHeight: 1.1}}>
                                {raceData.more.toUpperCase()}
                        </span>
                    }
                </Box>
                <div style={{
                    position: 'absolute',
                    right: raceData.firstname.length > 0 ? PRINT_SIZE[size].time.right : PRINT_SIZE[size].frame.padding,
                    bottom: PRINT_SIZE[size].frame.padding,
                    textAlign: 'right',
                    color: raceData.textColor
                }}>
                    {raceData.bip &&
                        <div style={{...PRINT_SIZE[theme.sizePrint].small}}>
                            N° {raceData.bip}
                        </div>
                    }
                    {!isNaN(raceData.hour) && !isNaN(raceData.minute) && !isNaN(raceData.second) &&
                        <div style={{
                            ...PRINT_SIZE[theme.sizePrint].title,
                            ...PRINT_SIZE[theme.sizePrint].timeLabel,
                            lineHeight: 0.8,
                            fontWeight: 'bold'
                        }}>
                            {String(raceData.hour).padStart(2, '0')}H{String(raceData.minute).padStart(2, '0')}'{String(raceData.second).padStart(2, '0')}''
                        </div>
                    }
                </div>
            </div>

        </Box>
        </Box>
    )
};

export default Theme;
